import React, { useEffect, useState } from 'react';
import { Search, Bell, User, Menu, Wallet, X, CheckCircle, AlertCircle, Info, Star, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../contexts/WalletContext';
import { useNotifications } from '../contexts/NotificationsContext';
import { useSolPrice } from '../contexts/SolPriceContext';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import SearchModal from './SearchModal';
import FullPieceDetailModal from './FullPieceDetailModal';
import RequestTradeModal from './RequestTradeModal';
import MobileMenuDrawer from './MobileMenuDrawer';
import { useWalletAction } from '../hooks/useWalletAction';
import { createOffer } from '../utils/offersContract';

const Header = () => {
  const { balance, usdPrice, isConnected, isConnecting, publicKey, wallet } = useWallet();
  const { notifications, unreadCount, markAllAsRead, deleteNotification, deleteAllNotifications } = useNotifications();
  const { withWalletCheck } = useWalletAction();
  const { solPrice } = useSolPrice();
  const navigate = useNavigate();
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [pieceDetailModal, setPieceDetailModal] = useState({ isOpen: false, item: null });
  const [offerModal, setOfferModal] = useState({ 
    isOpen: false, 
    item: null, 
    offerAmount: '', 
    expirationDays: 7, 
    expirationHours: 0 
  });
  const [requestTradeModal, setRequestTradeModal] = useState({ isOpen: false, item: null });
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Format wallet address for display
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  // Calculate USD value
  const usdValue = balance * usdPrice;

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'alert':
        return <AlertCircle className="w-5 h-5 text-yellow-400" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-400" />;
      default:
        return <Star className="w-5 h-5 text-purple-400" />;
    }
  };

  // Function to remove a notification
  const removeNotification = async (id) => {
    await deleteNotification(id);
  };

  // Function to show confirmation dialog
  const handleMarkAllAsRead = () => {
    setShowDeleteConfirm(true);
  };

  // Function to confirm and delete all notifications
  const confirmDeleteAll = async () => {
    await deleteAllNotifications();
    setShowDeleteConfirm(false);
  };

  // Function to cancel deletion
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  // Handle search - open modal
  const handleSearchClick = () => {
    setIsSearchModalOpen(true);
  };

  // Handle opening piece detail modal from search
  const handleOpenPieceDetail = (item) => {
    setPieceDetailModal({ isOpen: true, item });
  };

  // Close piece detail modal
  const closePieceDetailModal = () => {
    setPieceDetailModal({ isOpen: false, item: null });
  };

  // Handle make offer button
  const handleMakeOffer = withWalletCheck(async (item) => {
    // Check if user already has an active offer on this piece
    try {
      const { getPieceOffers } = await import('../utils/offersContract');
      const offersResult = await getPieceOffers(item.mint_address, 'active');
      
      if (offersResult.success && offersResult.offers.length > 0) {
        // Check if any of the active offers are from this user
        const userOffer = offersResult.offers.find(
          offer => offer.offerer_wallet === publicKey?.toString()
        );
        
        if (userOffer) {
          setSuccessMessage(`You already have an active offer of ${userOffer.offer_price} SOL on this piece. Visit the Offers page to view or update it.`);
          setShowSuccessNotification(true);
          setTimeout(() => setShowSuccessNotification(false), 4000);
          return;
        }
      }
    } catch (error) {
      console.log('Could not check for existing offers:', error);
      // Continue anyway - the backend will catch duplicates
    }
    
    // Close piece detail modal and open offer modal
    setPieceDetailModal({ isOpen: false, item: null });
    setOfferModal({ isOpen: true, item, offerAmount: '', expirationDays: 7, expirationHours: 0 });
  }, 'make an offer');

  // Process offer with wallet
  const processOffer = async () => {
    if (!isConnected || !publicKey) {
      setSuccessMessage('Wallet not connected');
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 4000);
      return;
    }

    const { item, offerAmount, expirationDays, expirationHours } = offerModal;
    const offerPrice = parseFloat(offerAmount);
    
    if (isNaN(offerPrice) || offerPrice <= 0) {
      setSuccessMessage('Please enter a valid offer amount');
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 4000);
      return;
    }
    
    if (balance < offerPrice) {
      setSuccessMessage(`Insufficient balance. You have ${balance.toFixed(4)} SOL but are offering ${offerPrice} SOL.`);
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 4000);
      return;
    }

    setIsProcessing(true);
    
    try {
      console.log('📝 Creating offer for:', item.name);
      console.log('Offer details:', {
        pieceId: item.id,
        mintAddress: item.mint_address,
        offerPrice,
        expirationDays,
        expirationHours
      });
      
      const result = await createOffer(wallet, {
        pieceId: item.id,
        mintAddress: item.mint_address,
        pieceName: item.name,
        offerPrice: offerPrice,
        message: '',
        expirationDays: expirationDays,
        expirationHours: expirationHours
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to create offer');
      }
      
      console.log('✅ Offer created successfully!');
      
      // Success!
      setSuccessMessage(`Offer of ${offerPrice} SOL submitted for ${item.name}! ✓`);
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 4000);
      
      // Close modal
      setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7, expirationHours: 0 });
      
    } catch (error) {
      console.error('❌ Offer failed:', error);
      setSuccessMessage(error.message || 'Unable to submit offer. Please try again.');
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 4000);
    } finally {
      setIsProcessing(false);
    }
  };

  // Close offer modal
  const closeOfferModal = () => {
    setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7, expirationHours: 0 });
  };

  // Handle request trade button
  const handleRequestTrade = withWalletCheck((item) => {
    // Close piece detail modal and open trade modal
    setPieceDetailModal({ isOpen: false, item: null });
    setRequestTradeModal({ isOpen: true, item });
  }, 'request a trade');

  // Handle trade request success
  const handleTradeRequestSuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessNotification(true);
    setTimeout(() => setShowSuccessNotification(false), 4000);
  };

  return (
    <>
    <header className="border-b border-white/10 bg-gradient-to-r from-black/95 via-black/90 to-black/95 backdrop-blur-md w-full z-10">
      {/* MOBILE LAYOUT - Explicit single row */}
      <div className="md:hidden w-full">
        <div className="flex flex-row items-center justify-start w-full px-2 py-2 gap-2" style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
          {/* Hamburger - Far Left */}
          <button 
            onClick={() => setIsMobileMenuOpen(true)}
            className="flex-shrink-0 p-1.5 rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Open menu"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Menu size={22} className="text-white" />
          </button>

          {/* Search Icon - Mobile Only */}
          <button 
            onClick={handleSearchClick}
            className="flex-shrink-0 p-1.5 rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Search"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Search size={20} className="text-white/70" />
          </button>

          {/* Spacer */}
          <div className="flex-1"></div>

          {/* Notification Bell */}
          <button 
            onClick={() => {
              console.log('Bell clicked, current state:', isNotificationsOpen);
              console.log('Notifications:', notifications);
              console.log('Unread count:', unreadCount);
              const newState = !isNotificationsOpen;
              console.log('Setting to:', newState);
              setIsNotificationsOpen(newState);
            }}
            className="flex-shrink-0 p-1.5 rounded-lg relative"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Bell size={16} className="text-white/70" />
            {unreadCount > 0 && (
              <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full border border-black"></div>
            )}
          </button>

          {/* Wallet */}
          <div className="flex-shrink-0">
            <WalletMultiButton 
              className="!bg-white/20 !border !border-white/30 hover:!bg-white/30 !text-white !font-semibold !px-2 !py-1 !rounded-lg !text-[10px] !transition-all !duration-200 hover:!scale-105 !min-w-[70px] !justify-center !shadow-lg hover:!shadow-white/25 !ring-0 !outline-none focus:!ring-2 focus:!ring-white/50 focus:!border-white/50"
            />
          </div>
        </div>
      </div>

      {/* DESKTOP LAYOUT */}
      <div className="hidden md:flex items-center justify-between w-full max-w-full px-4 lg:px-6 xl:px-8 py-3 lg:py-4">
        {/* Center - Search */}
        <div className="flex flex-1 max-w-md mx-4 lg:mx-8">
          <button 
            onClick={handleSearchClick}
            className="relative group w-full text-left"
          >
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/90 group-hover:text-white transition-colors duration-200 z-10" size={18} />
            <div className="w-full border border-white/10 rounded-xl pl-9 pr-4 py-2.5 sm:py-3 text-white/50 
              bg-white/5 backdrop-blur-sm group-hover:border-white/20 group-hover:bg-white/10 
              transition-all duration-200 text-sm sm:text-base cursor-pointer">
              Search Outsera
            </div>
            <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
          </button>
        </div>

        {/* Right side - Actions and Wallet */}
        <div className="flex items-center gap-2 lg:gap-3 flex-shrink-0">
          {/* Notifications */}
          <button 
            onClick={() => {
              console.log('Bell clicked, current state:', isNotificationsOpen);
              console.log('Notifications:', notifications);
              console.log('Unread count:', unreadCount);
              const newState = !isNotificationsOpen;
              console.log('Setting to:', newState);
              setIsNotificationsOpen(newState);
            }}
            className="p-2 rounded-xl group relative"
          >
            <Bell size={18} className="text-white/70" />
            {unreadCount > 0 && (
              <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border border-black"></div>
            )}
          </button>

          {/* Wallet Connection Button */}
          <div className="wallet-adapter-button-trigger">
            <WalletMultiButton 
              className="!bg-white/20 !border !border-white/30 hover:!bg-white/30 !text-white !font-semibold !px-4 !py-2.5 !rounded-xl !text-sm !transition-all !duration-200 hover:!scale-105 !min-w-[100px] !justify-center !shadow-lg hover:!shadow-white/25 !ring-0 !outline-none focus:!ring-2 focus:!ring-white/50 focus:!border-white/50"
            />
          </div>
        </div>
      </div>
    </header>

    {/* Mobile Menu Drawer */}
    <MobileMenuDrawer 
      isOpen={isMobileMenuOpen}
      onClose={() => setIsMobileMenuOpen(false)}
    />

    {/* Search Modal */}
    <SearchModal 
      isOpen={isSearchModalOpen} 
      onClose={() => setIsSearchModalOpen(false)}
      onOpenPieceDetail={handleOpenPieceDetail}
    />

    {/* Piece Detail Modal */}
    <FullPieceDetailModal
      isOpen={pieceDetailModal.isOpen}
      item={pieceDetailModal.item}
      onClose={closePieceDetailModal}
      onMakeOffer={handleMakeOffer}
      onRequestTrade={handleRequestTrade}
    />

    {/* Make Offer Modal */}
    {offerModal.isOpen && (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={closeOfferModal}>
        <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
              <Package size={24} className="text-blue-500" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-white">Make an Offer</h3>
              <p className="text-gray-400 text-sm">Submit your offer for this piece</p>
            </div>
          </div>
          
          <div className="mb-6">
            <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
              <h4 className="text-white font-medium mb-2">{offerModal.item?.name}</h4>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-sm">List Price</span>
                <div className="text-right">
                  <div className="text-white font-bold text-lg">{offerModal.item?.price} SOL</div>
                  <div className="text-gray-500 text-sm">
                    ≈ ${(parseFloat(offerModal.item?.price || '0') * solPrice).toFixed(2)} USD
                  </div>
                </div>
              </div>
            </div>

            {/* Offer Amount Input */}
            <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
              <label className="block text-white font-medium mb-3">Your Offer Amount</label>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={offerModal.offerAmount}
                  onChange={(e) => setOfferModal(prev => ({ ...prev, offerAmount: e.target.value }))}
                  placeholder="0.00"
                  className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                  SOL
                </div>
              </div>
              {offerModal.offerAmount && !isNaN(parseFloat(offerModal.offerAmount)) && (
                <div className="mt-2 text-right text-gray-500 text-sm">
                  ≈ ${(parseFloat(offerModal.offerAmount) * solPrice).toFixed(2)} USD
                </div>
              )}
            </div>

            {/* Offer Expiration Time */}
            <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
              <label className="block text-white font-medium mb-3">Offer Expiration</label>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-gray-400 text-sm mb-2">Days</label>
                  <input
                    type="number"
                    min="0"
                    max="30"
                    value={offerModal.expirationDays === 0 ? '' : offerModal.expirationDays}
                    onChange={(e) => setOfferModal(prev => ({ ...prev, expirationDays: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 }))}
                    placeholder="0"
                    className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-gray-400 text-sm mb-2">Hours</label>
                  <input
                    type="number"
                    min="0"
                    max="23"
                    value={offerModal.expirationHours === 0 ? '' : offerModal.expirationHours}
                    onChange={(e) => setOfferModal(prev => ({ ...prev, expirationHours: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 }))}
                    placeholder="0"
                    className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="mt-2 text-gray-500 text-sm">
                Offer will expire in {offerModal.expirationDays} day{offerModal.expirationDays !== 1 ? 's' : ''} and {offerModal.expirationHours} hour{offerModal.expirationHours !== 1 ? 's' : ''}
              </div>
            </div>

            {/* Wallet Info */}
            <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
              <h4 className="text-white font-medium mb-3">Wallet Information</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Connected Wallet</span>
                  <div className="flex items-center gap-2">
                    <Wallet size={16} className="text-green-400" />
                    <span className="text-green-400 text-sm font-medium">
                      {isConnected ? 'Connected' : 'Not Connected'}
                    </span>
                  </div>
                </div>
                {isConnected && publicKey && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Address</span>
                    <span className="text-white text-sm font-mono">
                      {publicKey.toString().slice(0, 8)}...{publicKey.toString().slice(-8)}
                    </span>
                  </div>
                )}
                {isConnected && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Balance</span>
                    <div className="text-right">
                      <div className="text-white font-medium">{balance.toFixed(4)} SOL</div>
                      <div className="text-gray-500 text-xs">
                        ≈ ${(balance * solPrice).toFixed(2)} USD
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="text-gray-300 text-sm">
              Your offer will be locked in escrow until the seller accepts or rejects it, or until it expires. You can cancel your offer at any time before expiration.
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={closeOfferModal}
              disabled={isProcessing}
              className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={processOffer}
              disabled={isProcessing || !isConnected || !offerModal.offerAmount || isNaN(parseFloat(offerModal.offerAmount)) || parseFloat(offerModal.offerAmount) <= 0 || balance < parseFloat(offerModal.offerAmount || '0')}
              className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Package size={16} />
                  Submit Offer
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Request Trade Modal */}
    <RequestTradeModal
      isOpen={requestTradeModal.isOpen}
      onClose={() => setRequestTradeModal({ isOpen: false, item: null })}
      targetPiece={requestTradeModal.item}
      onSuccess={handleTradeRequestSuccess}
    />

    {/* Notifications Sidebar - Outside header */}
    {isNotificationsOpen && (
      <>
        {/* Invisible backdrop for closing */}
        <div 
          className="fixed inset-0 z-[100] bg-black/20"
          onClick={() => setIsNotificationsOpen(false)}
        />
        
        {/* Notifications Sidebar */}
        <div className="fixed top-0 right-0 h-full w-80 sm:w-96 bg-black border-l border-white/10 z-[101] shadow-2xl" style={{backgroundColor: '#000000'}}>
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2 className="text-lg font-semibold text-white">Notifications</h2>
              <button 
                onClick={() => setIsNotificationsOpen(false)}
                className="p-1 rounded-lg hover:bg-white/10 transition-colors duration-200"
              >
                <X size={20} className="text-white/70 hover:text-white" />
              </button>
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-6">
                  <Bell size={48} className="text-white/30 mb-4" />
                  <p className="text-white/60 text-sm">No notifications yet</p>
                </div>
              ) : (
                <div className="p-2">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className="p-3 rounded-xl mb-2 transition-all duration-200 hover:bg-white/5 group relative bg-white/5"
                    >
                      {/* Close button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeNotification(notification.id);
                        }}
                        className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/10 transition-colors duration-200 opacity-0 group-hover:opacity-100"
                        title="Dismiss notification"
                      >
                        <X size={14} className="text-white/50 hover:text-white" />
                      </button>

                      <div className="flex items-start gap-3 pr-6">
                        <div className="flex-shrink-0 mt-0.5">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h3 className="text-sm font-medium text-white truncate">
                              {notification.title}
                            </h3>
                          </div>
                          <p className="text-xs text-white/70 mt-1 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-white/50 mt-1">
                            {notification.time}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-white/10">
              {!showDeleteConfirm ? (
                <button 
                  onClick={handleMarkAllAsRead}
                  disabled={notifications.length === 0}
                  className={`w-full py-2 px-4 rounded-xl text-sm font-medium transition-all duration-200 ${
                    notifications.length === 0 
                      ? 'bg-gray-500/20 border border-gray-500/30 cursor-not-allowed opacity-50 text-gray-400' 
                      : 'bg-white/20 border border-white/30 text-white hover:bg-white/30'
                  }`}
                >
                  Mark as Read
                </button>
              ) : (
                /* Confirmation View */
                <div className="space-y-3">
                  {/* Icon and Message */}
                  <div className="flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-white mb-1">
                        Delete All Notifications?
                      </p>
                      <p className="text-xs text-white/70">
                        This will delete all notifications. This action cannot be undone.
                      </p>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={cancelDelete}
                      className="flex-1 py-2 px-3 rounded-xl text-sm font-medium bg-white/10 border border-white/20 text-white hover:bg-white/15 transition-all duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmDeleteAll}
                      className="flex-1 py-2 px-3 rounded-xl text-sm font-medium bg-red-500/90 border border-red-500 text-white hover:bg-red-500 transition-all duration-200"
                    >
                      Delete All
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    )}

    {/* Success Notification Toast */}
    {showSuccessNotification && (
      <div className="fixed top-4 right-4 z-[70] animate-in fade-in slide-in-from-top-4 duration-300">
        <div className="bg-green-600 border border-green-500/30 rounded-xl p-4 shadow-lg max-w-sm">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <CheckCircle size={14} className="text-white" />
            </div>
            <div className="flex-1">
              <p className="text-white font-medium text-sm">{successMessage}</p>
            </div>
            <button
              onClick={() => setShowSuccessNotification(false)}
              className="text-white/70 hover:text-white transition-colors flex-shrink-0"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};


export default Header; 